import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  IconButton,
  Chip,
  Tooltip,
} from "@mui/material";
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import { UOMTypeCreate, UOMTypeUpdate } from "../../types/uomTypes";
import {
  useTSQUOMTypeId,
  useTSMUpdateUOMType,
  useTSMCreateUOMType,
} from "../../hooks/useTSUnits";

// Validation schema based on API requirements
const uomTypeSchema = yup.object({
  type_name: yup.string().required("Type name is required"),
  units: yup
    .array()
    .of(
      yup.object({
        unit_name: yup.string().required("Unit name is required"),
        unit_symbol: yup.string().required("Unit symbol is required"),
        conversion_factor: yup
          .number()
          .positive("Conversion factor must be positive")
          .required("Conversion factor is required"),
      })
    )
    .min(1, "At least one unit is required"),
  is_record_active: yup.boolean().optional(),
  description: yup.string().optional(),
});

export const PageUOMTypeForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Mode detection from URL query parameters
  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get("id");
  const formMode = searchParams.get("mode") || "create";
  const isViewMode = formMode === "view";
  const isEditMode = Boolean(id) && formMode === "edit";
  const isCreateMode = !id && formMode === "create";

  const [error, setError] = useState<string | null>(null);
  // const [oldBase, setOldBase] = useState<UOMUnitUpdate | null>(null);
  // const [newBase, setNewBase] = useState<UOMUnitUpdate | UOMUnitCreate | null>(null);

  const {
    data: UOMTypeData,
    isLoading: loadingUOMType,
    error: UOMTypeError,
  } = useTSQUOMTypeId(id || "");

  // const [loading, setLoading] = useState(false);
  // const [loadingUomType, setLoadingUomType] = useState(
  //   isEditMode || isViewMode
  // );
  // const [uomType, setUomType] = useState<UOMTypeRead | null>(null);

  interface UOMTypeFormData {
    type_name: string;
    units: UOMUnitFormData[];
    is_record_active: boolean;
    description?: string;
  }

  interface UOMUnitFormData {
    id?: string;
    unit_name: string;
    unit_symbol: string;
    plural_symbol: string;
    plural_name: string;
    conversion_factor: number;
    is_base_unit: boolean;
    _isOldBase?: boolean;
    _isNew?: boolean;
    _isDeleted?: boolean;
    _isModified?: boolean;
    _tempId?: string;
  }

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
  } = useForm<UOMTypeFormData>({
    resolver: yupResolver(uomTypeSchema) as any,
    defaultValues: {
      type_name: "",
      units: [],
      is_record_active: true,
      description: "",
    },
  });

  const [UOMUnits, setUOMUnits] = useState<UOMUnitFormData[]>([]);

  const createUOMTypeMutation = useTSMCreateUOMType();
  const updateUOMTypeMutation = useTSMUpdateUOMType();

  // Mode switching functions
  const switchToViewMode = () => {
    navigate(`/inventory/uoms/form?mode=view&id=${id}`);
  };

  const switchToEditMode = () => {
    navigate(`/inventory/uoms/form?mode=edit&id=${id}`);
  };

  const switchToCreateMode = () => {
    navigate("/inventory/uoms/form?mode=create");
  };

  const updateUOMUnits = useCallback(
    (newUnits: UOMUnitFormData[]) => {
      setUOMUnits(newUnits);
      setValue("units", newUnits);
    },
    [setValue]
  );

  const addUnit = () => {
    const newUnit: UOMUnitFormData = {
      unit_name: "",
      unit_symbol: "",
      plural_symbol: "",
      plural_name: "",
      conversion_factor: 1,
      is_base_unit: UOMUnits.length === 0,
      _isOldBase: false,
      _isNew: true,
      _isModified: false,
      _isDeleted: false,
      _tempId: `temp-${Date.now()}-${Math.random()}`,
    };
    updateUOMUnits([...UOMUnits, newUnit]);
  };

  const updateUnit = (id: string, field: keyof UOMUnitFormData, value: any) => {
    const updatedUnits = [...UOMUnits];
    const unit = updatedUnits.find(
      (unit) => unit.id === id || unit._tempId === id
    );
    if (!unit) {
      throw Error("Unable to determine unit to update.");
    }
    if (!unit._isNew) {
      unit._isModified = true;
    }
    const index = updatedUnits.indexOf(unit);

    if (field === "is_base_unit" && value === true) {
      updatedUnits.forEach((u, i) => {
        if (i !== index) {
          if (u.is_base_unit) {
            u.is_base_unit = false;
            if (!u._isNew) u._isModified = true;
          }
        }
      });
      unit.conversion_factor = 1;
    }

    (unit as any)[field] = value;
    updateUOMUnits(updatedUnits);
  };

  const deleteUnit = (id: string) => {
    const unit = UOMUnits.find((unit) => unit.id === id || unit._tempId === id);
    if (!unit) {
      throw Error("Unable to determine unit to delete.");
    }
    const index = UOMUnits.indexOf(unit);

    if (unit.is_base_unit && UOMUnits.filter((u) => !u._isDeleted).length > 1) {
      setError(
        "Cannot delete the base unit. Please set another unit as base first."
      );
      return;
    }

    if (unit._isNew) {
      // Remove new units completely
      updateUOMUnits(
        UOMUnits.filter((v, i) => v.id !== id && v._tempId !== id)
      );
    } else {
      // Mark existing units as deleted
      const updatedUnits = [...UOMUnits];
      updatedUnits[index]._isDeleted = true;
      updateUOMUnits(updatedUnits);
    }
  };

  const validateUnits = () => {
    const activeUnits = UOMUnits.filter((u) => !u._isDeleted);
    const unitNames = activeUnits.map((u) => u.unit_name.toLowerCase().trim());
    const unitSymbols = activeUnits.map((u) =>
      u.unit_symbol.toLowerCase().trim()
    );

    // Check for duplicates
    const duplicateNames = unitNames.filter(
      (name, index) => unitNames.indexOf(name) !== index
    );
    const duplicateSymbols = unitSymbols.filter(
      (symbol, index) => unitSymbols.indexOf(symbol) !== index
    );

    if (duplicateNames.length > 0) {
      setError(`Duplicate unit names found: ${duplicateNames.join(", ")}`);
      return false;
    }

    if (duplicateSymbols.length > 0) {
      setError(`Duplicate unit symbols found: ${duplicateSymbols.join(", ")}`);
      return false;
    }

    // Check for empty fields
    const hasEmptyFields = activeUnits.some(
      (u) =>
        !u.unit_name.trim() || !u.unit_symbol.trim() || u.conversion_factor <= 0
    );

    if (hasEmptyFields) {
      setError(
        "All unit fields are required and conversion factors must be positive."
      );
      return false;
    }

    // Check for base unit
    const baseUnits = activeUnits.filter((u) => u.is_base_unit);
    if (baseUnits.length !== 1) {
      setError("Exactly one unit must be designated as the base unit.");
      return false;
    }

    return true;
  };

  // Load UOM type data for edit/view mode
  useEffect(() => {
    if (UOMTypeData && (isEditMode || isViewMode)) {
      reset({
        type_name: UOMTypeData.type_name,
        is_record_active: UOMTypeData.is_record_active,
        description: UOMTypeData.description || "",
        units: [],
      });

      const initUnits = UOMTypeData.all_units.map((unit) => ({
        id: unit.id,
        unit_name: unit.unit_name,
        unit_symbol: unit.unit_symbol,
        plural_symbol: unit.plural_symbol,
        plural_name: unit.plural_name,
        conversion_factor: parseFloat(unit.conversion_factor),
        is_base_unit: unit.is_base_unit,
        _isOldBase: unit.is_base_unit,
        _isNew: false,
        _isModified: false,
        _isDeleted: false,
      }));
      updateUOMUnits(initUnits);
    }
  }, [updateUOMUnits, UOMTypeData, isEditMode, isViewMode, reset]);

  const onSubmit = async (data: UOMTypeFormData) => {
    try {
      setError(null);
      if (!validateUnits()) {
        return;
      }

      if (isCreateMode) {
        const baseUnit = UOMUnits.find((u) => u.is_base_unit) || UOMUnits[0];
        const additionalUnits = UOMUnits.filter((u) => !u.is_base_unit);

        const createData: UOMTypeCreate = {
          type_name: data.type_name,
          base_unit: {
            unit_name: baseUnit.unit_name,
            unit_symbol: baseUnit.unit_symbol,
            plural_symbol: baseUnit.plural_symbol,
            plural_name: baseUnit.plural_name,
            conversion_factor: baseUnit.conversion_factor,
            is_base_unit: true,
          },
          additional_units: additionalUnits.map((unit) => ({
            unit_name: unit.unit_name,
            unit_symbol: unit.unit_symbol,
            plural_symbol: unit.plural_symbol,
            plural_name: unit.plural_name,
            conversion_factor: unit.conversion_factor,
            is_base_unit: false,
          })),
          is_record_active: data.is_record_active,
          description: data.description || "",
        };
        const new_uom = await createUOMTypeMutation.mutateAsync(createData);
        navigate(`/inventory/uoms/form?mode=view&id=${new_uom.id}`);
      } else if (isEditMode && id) {
        const origBaseUnit = UOMUnits.find((unit) => unit._isOldBase);
        if (!origBaseUnit) throw Error("Could not find original base unit");
        const newBaseUnit = UOMUnits.find(
          (unit) => unit.is_base_unit && !unit._isDeleted
        );
        const baseUnitChanged = origBaseUnit?.id !== newBaseUnit?.id;
        const nonBaseUnits = UOMUnits.filter(
          (unit) => !unit.is_base_unit && unit.id !== origBaseUnit?.id
        );
        let oldBase = {
          id: origBaseUnit.id,
          unit_name: origBaseUnit.unit_name,
          unit_symbol: origBaseUnit.unit_symbol,
          conversion_factor: origBaseUnit.conversion_factor,
          is_base_unit: false,
          is_delete: origBaseUnit._isDeleted,
        };
        let newBase = undefined;

        if (baseUnitChanged && origBaseUnit && newBaseUnit) {
          newBase = {
            id: newBaseUnit.id,
            unit_name: newBaseUnit.unit_name,
            unit_symbol: newBaseUnit.unit_symbol,
            conversion_factor: 1,
            is_base_unit: true,
          };
        }

        const updateData: UOMTypeUpdate = {
          id: id,
          type_name: data.type_name,
          description: data.description || "",
          is_record_active: data.is_record_active,
          new_units: nonBaseUnits
            .filter((u) => u._isNew && !u._isDeleted)
            .map((unit) => ({
              uom_type_id: id,
              unit_name: unit.unit_name,
              unit_symbol: unit.unit_symbol,
              plural_name: unit.plural_name,
              plural_symbol: unit.plural_symbol,
              conversion_factor: unit.conversion_factor,
              is_base_unit: unit.is_base_unit,
            })),
          updated_units: nonBaseUnits
            .filter((u) => u._isModified && !u._isNew && !u._isDeleted)
            .map((unit) => ({
              id: unit.id,
              unit_name: unit.unit_name,
              unit_symbol: unit.unit_symbol,
              plural_name: unit.plural_name,
              plural_symbol: unit.plural_symbol,
              conversion_factor: unit.conversion_factor,
              is_base_unit: unit.is_base_unit,
            })),
          deleted_units: [
            ...nonBaseUnits
              .filter((u) => u._isDeleted)
              .map((u) => u.id)
              .filter((x): x is string => x !== undefined),
          ],
          old_base: oldBase,
          new_base: newBase,
        };
        await updateUOMTypeMutation.mutateAsync({ id, data: updateData });
        navigate(`/inventory/uoms/form?mode=view&id=${id}`);
      }
    } catch (err: any) {
      setError(
        err.response?.data?.detail || err.message || "Failed to save UOM type"
      );
      console.error("Error saving UOM type:", err);
    }
  };

  const handleCancel = () => {
    navigate("/inventory/uoms");
  };

  if (loadingUOMType) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleCancel}>
          Back to UOM Types
        </Button>
        <Typography variant="h4" component="h1">
          {isCreateMode
            ? "Create New UOM Type"
            : isViewMode
            ? `View UOM Type: ${UOMTypeData?.type_name || ""}`
            : `Edit UOM Type: ${UOMTypeData?.type_name || ""}`}
        </Typography>
        {formMode === "view" && (
          <Button
            variant="outlined"
            onClick={switchToCreateMode}
            startIcon={<AddIcon />}
          >
            Create New
          </Button>
        )}
        {isViewMode && (
          <Button
            variant="outlined"
            onClick={switchToEditMode}
            startIcon={<EditIcon />}
          >
            Edit
          </Button>
        )}
        {isEditMode && (
          <Button
            variant="outlined"
            onClick={switchToViewMode}
            startIcon={<VisibilityIcon />}
          >
            View
          </Button>
        )}
      </Stack>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Form */}
      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit(onSubmit, console.error)}>
          <Stack spacing={3}>
            {/* Basic Information */}
            <Typography variant="h6" gutterBottom>
              UOM Type Information
            </Typography>

            <Controller
              name="type_name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Type Name *"
                  fullWidth
                  disabled={isViewMode}
                  error={!!errors.type_name}
                  helperText={
                    errors.type_name?.message ||
                    "e.g., Weight, Volume, Length, Count"
                  }
                  placeholder="Enter the measurement category name"
                />
              )}
            />

            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  disabled={isViewMode}
                  error={!!errors.description}
                  helperText={
                    errors.description?.message ||
                    "Optional description of this UOM type"
                  }
                  placeholder="Describe what this UOM type is used for..."
                />
              )}
            />

            <Divider />

            {/* UOM Units Management */}
            <Typography variant="h6" gutterBottom>
              UOM Units
            </Typography>

            <Alert severity="info" sx={{ mb: 2 }}>
              Define the units for this UOM type. The first unit (marked with
              ⭐) is the base unit with a conversion factor of 1.0. All other
              units are defined relative to the base unit.
            </Alert>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Base</TableCell>
                    <TableCell>Unit Name *</TableCell>
                    <TableCell>Symbol *</TableCell>
                    <TableCell>Conversion Factor *</TableCell>
                    {!isViewMode && (
                      <TableCell align="center">Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {UOMUnits.filter((u) => !u._isDeleted).length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={isViewMode ? 4 : 5}
                        align="center"
                        sx={{ py: 3 }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No units defined.{" "}
                          {!isViewMode && "Click 'Add Unit' to get started."}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    UOMUnits.filter((u) => !u._isDeleted).map((unit, index) => {
                      const uniqueKey = unit.id || unit._tempId || "";
                      return (
                        <TableRow
                          key={uniqueKey}
                          sx={{
                            backgroundColor: unit.is_base_unit
                              ? "action.hover"
                              : "inherit",
                            "&:hover": {
                              backgroundColor: unit.is_base_unit
                                ? "action.selected"
                                : "action.hover",
                            },
                          }}
                        >
                          <TableCell>
                            {unit.is_base_unit ? (
                              <Tooltip title="Base Unit">
                                <StarIcon color="primary" fontSize="small" />
                              </Tooltip>
                            ) : (
                              !isViewMode && (
                                <Tooltip title="Set as Base Unit">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      updateUnit(
                                        uniqueKey,
                                        "is_base_unit",
                                        true
                                      )
                                    }
                                    disabled={isSubmitting}
                                  >
                                    <StarIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )
                            )}
                          </TableCell>
                          <TableCell>
                            <Stack direction="column" spacing={0.5}>
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ mr: 1 }}
                                >
                                  Singular:
                                </Typography>
                                <TextField
                                  value={unit.unit_name}
                                  onChange={(e) =>
                                    updateUnit(
                                      uniqueKey,
                                      "unit_name",
                                      e.target.value
                                    )
                                  }
                                  placeholder="e.g., Kilogram, Pound"
                                  size="small"
                                  fullWidth
                                  disabled={isViewMode || isSubmitting}
                                  error={!unit.unit_name.trim() && !isViewMode}
                                  required
                                />
                              </Box>
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ mr: 1 }}
                                >
                                  Plural:
                                </Typography>
                                <TextField
                                  value={unit.plural_name}
                                  onChange={(e) =>
                                    updateUnit(
                                      uniqueKey,
                                      "plural_name",
                                      e.target.value
                                    )
                                  }
                                  placeholder="e.g., Kilograms, Pounds"
                                  size="small"
                                  fullWidth
                                  disabled={isViewMode || isSubmitting}
                                  error={!unit.unit_name.trim() && !isViewMode}
                                  required
                                />
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Stack direction="column" spacing={0.5}>
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ mr: 1 }}
                                >
                                  Singular:
                                </Typography>
                                <TextField
                                  value={unit.unit_symbol}
                                  onChange={(e) =>
                                    updateUnit(
                                      uniqueKey,
                                      "unit_symbol",
                                      e.target.value
                                    )
                                  }
                                  placeholder="e.g., kg, lb"
                                  size="small"
                                  disabled={isViewMode || isSubmitting}
                                  error={
                                    !unit.unit_symbol.trim() && !isViewMode
                                  }
                                  sx={{ minWidth: 80 }}
                                  required
                                />
                              </Box>
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ mr: 1 }}
                                >
                                  Plural:
                                </Typography>
                                <TextField
                                  value={unit.plural_symbol}
                                  onChange={(e) =>
                                    updateUnit(
                                      uniqueKey,
                                      "plural_symbol",
                                      e.target.value
                                    )
                                  }
                                  placeholder="e.g., kgs, lbs"
                                  size="small"
                                  disabled={isViewMode || isSubmitting}
                                  error={
                                    !unit.unit_symbol.trim() && !isViewMode
                                  }
                                  sx={{ minWidth: 80 }}
                                  required
                                />
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              value={unit.conversion_factor}
                              onChange={(e) =>
                                updateUnit(
                                  uniqueKey,
                                  "conversion_factor",
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              placeholder="1.0"
                              size="small"
                              disabled={
                                isViewMode || isSubmitting || unit.is_base_unit
                              }
                              error={unit.conversion_factor <= 0 && !isViewMode}
                              sx={{ minWidth: 120 }}
                              inputProps={{
                                min: 0,
                                step: 0.001,
                                style: {
                                  backgroundColor: unit.is_base_unit
                                    ? "#f5f5f5"
                                    : "inherit",
                                },
                              }}
                              helperText={
                                unit.is_base_unit ? "Base unit = 1.0" : ""
                              }
                              required
                            />
                          </TableCell>
                          {!isViewMode && (
                            <TableCell align="center">
                              <Tooltip
                                title={
                                  unit.is_base_unit
                                    ? "Cannot delete base unit"
                                    : "Delete unit"
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => deleteUnit(uniqueKey)}
                                    disabled={
                                      isSubmitting ||
                                      (unit.is_base_unit &&
                                        UOMUnits.filter((u) => !u._isDeleted)
                                          .length > 1)
                                    }
                                    color="error"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {!isViewMode && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addUnit}
                sx={{ mb: 2 }}
                disabled={isSubmitting}
              >
                Add Unit
              </Button>
            )}

            {/* Units Summary */}
            {UOMUnits.filter((u) => !u._isDeleted).length > 0 && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: "background.paper",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  <strong>Units Summary:</strong>{" "}
                  {UOMUnits.filter((u) => !u._isDeleted).length} unit(s) defined
                  {UOMUnits.filter((u) => u.is_base_unit && !u._isDeleted)
                    .length > 0 && (
                    <span>
                      {" "}
                      • Base Unit:{" "}
                      {UOMUnits.find((u) => u.is_base_unit && !u._isDeleted)
                        ?.unit_name || "Not set"}
                    </span>
                  )}
                </Typography>
              </Box>
            )}

            {/* Status */}
            <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
              <Controller
                name="is_record_active"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={field.onChange}
                        disabled={isViewMode}
                      />
                    }
                    label="Active UOM Type"
                  />
                )}
              />
            </Box>

            {/* Additional Information for View/Edit Mode */}
            {UOMTypeData && (isViewMode || isEditMode) && (
              <>
                <Divider />
                <Typography variant="h6" gutterBottom>
                  Additional Information
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                    gap: 2,
                  }}
                >
                  <TextField
                    label="Total Units"
                    value={UOMTypeData.all_units?.length || 0}
                    disabled
                    helperText="Number of units defined for this type"
                  />
                  <TextField
                    label="Created Date"
                    value={new Date(
                      UOMTypeData.created_at
                    ).toLocaleDateString()}
                    disabled
                    helperText="When this UOM type was created"
                  />
                  <TextField
                    label="Last Updated"
                    value={new Date(
                      UOMTypeData.updated_at
                    ).toLocaleDateString()}
                    disabled
                    helperText="When this UOM type was last modified"
                  />
                </Box>
              </>
            )}

            {/* Form Actions */}
            {!isViewMode && (
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "flex-end",
                  mt: 3,
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                  disabled={
                    isSubmitting ||
                    createUOMTypeMutation.isPending ||
                    updateUOMTypeMutation.isPending
                  }
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    createUOMTypeMutation.isPending ||
                    updateUOMTypeMutation.isPending ? (
                      <CircularProgress size={20} />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  disabled={
                    isSubmitting ||
                    createUOMTypeMutation.isPending ||
                    updateUOMTypeMutation.isPending
                  }
                >
                  {createUOMTypeMutation.isPending ||
                  updateUOMTypeMutation.isPending
                    ? "Saving..."
                    : isCreateMode
                    ? "Create UOM Type"
                    : "Update UOM Type"}
                </Button>
              </Box>
            )}
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};
